/**
 * <line-item-repeater>
 *
 * Keeps one line item property field per unit in the product form's quantity input,
 * e.g. quantity 3 renders properties[Bag 1], properties[Bag 2] and properties[Bag 3].
 *
 * Row 1 is rendered server side so the field still works with JS disabled. Rows 2..n are
 * cloned from the [data-row-template] <template>, with the literal `__n__` replaced by the
 * row number. Fields live outside the <form> and are associated with it through their
 * `form` attribute, so FormData picks them up on add to cart.
 *
 * Expects:
 *   data-form-id  id of the product form these fields belong to
 *   data-max      maximum number of fields to render
 */
class LineItemRepeater extends HTMLElement {
    constructor() {
        super();
        this.handleQuantityEvent = this.handleQuantityEvent.bind(this);
    }

    connectedCallback() {
        this.rowsContainer = this.querySelector('[data-rows]');
        this.rowTemplate = this.querySelector('[data-row-template]');
        this.formId = this.dataset.formId;
        this.max = parseInt(this.dataset.max, 10) || 1;

        if (!this.rowsContainer || !this.rowTemplate || !this.formId) return;

        // Delegated on document because the quantity selector is a sibling block that may
        // be rendered after this one, and because the theme's own change event is debounced.
        document.addEventListener('input', this.handleQuantityEvent);
        document.addEventListener('change', this.handleQuantityEvent);
        document.addEventListener('on:QuantityChange', this.handleQuantityEvent);

        // Quantity can start above 1 (quantity rules), and the quantity selector is a
        // sibling block that may not be in the DOM yet. A timeout rather than rAF so this
        // still runs when the page is rendered in a background tab.
        setTimeout(() => this.syncRows(), 0);
    }

    disconnectedCallback() {
        document.removeEventListener('input', this.handleQuantityEvent);
        document.removeEventListener('change', this.handleQuantityEvent);
        document.removeEventListener('on:QuantityChange', this.handleQuantityEvent);
    }

    // Only react to the quantity input of our own form. Cart drawer line items and other
    // product forms on the page dispatch the same events.
    handleQuantityEvent(event) {
        const target = event.target;
        if (!target || target.name !== 'quantity') return;
        if (target.getAttribute('form') !== this.formId) return;
        this.syncRows(target.value);
    }

    get quantityInput() {
        const form = document.getElementById(this.formId);
        const field = form && form.elements ? form.elements.quantity : null;
        // A RadioNodeList is returned if more than one control shares the name.
        if (field instanceof RadioNodeList) return field[0];
        return field || null;
    }

    syncRows(value) {
        const raw = value !== undefined ? value : this.quantityInput && this.quantityInput.value;
        const quantity = Math.min(Math.max(parseInt(raw, 10) || 1, 1), this.max);
        const current = this.rowsContainer.querySelectorAll('[data-row]').length;

        if (quantity === current) return;

        if (quantity > current) {
            let markup = '';
            for (let index = current + 1; index <= quantity; index += 1) {
                markup += this.rowTemplate.innerHTML.replace(/__n__/g, index);
            }
            this.rowsContainer.insertAdjacentHTML('beforeend', markup);
        } else {
            // Trim from the end so anything the customer already typed is kept.
            const rows = this.rowsContainer.querySelectorAll('[data-row]');
            for (let index = rows.length - 1; index >= quantity; index -= 1) {
                rows[index].remove();
            }
        }
    }
}

customElements.get('line-item-repeater') ||
    customElements.define('line-item-repeater', LineItemRepeater);
